import React, { useState } from "react"
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  useToast,
} from "@chakra-ui/react"
import { gql, useLazyQuery } from "@apollo/client"
import { useParams } from "react-router-dom"

import { pdfVersionControl } from "../../utils/helpers"
import {
  flushAllPendingSignatureStrokes,
  waitForPendingSignatureUploads,
} from "../../utils/signatureUploadService"
import { SignatureApproval } from "../FormControls"
import { toastSuccess, toastFailed } from "../../bootstrap/config"
import useIsIpad from "../../hooks/useIsIpad"
import { useProjectsContext } from "../../contexts/ProjectsProvider"
import useUpdateForm from "../../hooks/useUpdateForm"
import useGetFormStatuses from "../../hooks/useGetFormStatus"
import { useAppContext } from "../../contexts/AppProvider"

const GEN_PROJECT_PDF = gql`
  query ($values: GenProjectPdfInput!) {
    genProjectPDF(genProjectPdfInput: $values) {
      generated
    }
  }
`

interface MarkAsCompleteModalProps {
  control: any
  errors: any
  trigger: any
  isOpen: boolean
  onClose: () => void
  getValues: any
  handleSubmit: any
  formIndex: number
  iteration: number | null
  refetch: any
  savedValues: any
  formIteration: any
  cancelAutoSave: () => void
}

const MarkAsCompleteModal: React.FC<MarkAsCompleteModalProps> = ({
  control,
  errors: _errors,
  trigger: _trigger,
  isOpen,
  onClose,
  getValues,
  handleSubmit,
  formIndex,
  iteration,
  refetch,
  savedValues,
  formIteration,
  cancelAutoSave,
}) => {
  const { settings } = useAppContext()
  const { _id } = useParams()
  const [submitting, setSubmitting] = useState(false)
  const [isSignAndCompleteDisabled, setIsSignAndCompleteDisabled] =
    useState<boolean>(false)
  const toast = useToast()
  const { currentProject, currentProjectForm, formStates } =
    useProjectsContext()
  const { refetch: formStatusRefetch } = useGetFormStatuses(_id)
  const { updateFunction } = useUpdateForm()
  const ipad = useIsIpad()
  const [genProjectPDF] = useLazyQuery(GEN_PROJECT_PDF, {
    fetchPolicy: "network-only",
  })

  const runMarkAsComplete = async () => {
    cancelAutoSave?.()
    // Let debounced auto-save cancel flush before we read values
    await new Promise((r) => setTimeout(r, 100))

    const formValues = getValues()

    const currentFormFieldNames = new Set(
      currentProjectForm?.pages?.flatMap((page) =>
        page?.sections?.flatMap((section) =>
          section?.rows?.flatMap(
            (row) => row?.fields?.map((field) => field.name) || []
          ) || []
        ) || []
      ) || []
    )

    const systemFields = new Set([
      "completedSignedDate",
      "pdfFirstPublished",
      "completedBySignature",
    ])

    const existingValues = formIteration?.values || savedValues?.current || {}

    // Keep keys already saved on the iteration even if the visible template
    // (override) no longer lists them — older in-progress forms must not lose data.
    const filteredFormValues = Object.keys(formValues).reduce((acc, key) => {
      if (
        currentFormFieldNames.size === 0 ||
        currentFormFieldNames.has(key) ||
        systemFields.has(key) ||
        Object.prototype.hasOwnProperty.call(existingValues, key)
      ) {
        acc[key] = formValues[key]
      } else {
        console.warn(
          `[MarkAsComplete] Filtering out field "${key}" - not in form structure and not a system field`
        )
      }
      return acc
    }, {} as any)

    const mergedValues = {
      ...existingValues,
      ...filteredFormValues,
      completedSignedDate: new Date(),
      pdfFirstPublished:
        filteredFormValues.pdfFirstPublished ||
        existingValues.pdfFirstPublished ||
        new Date(),
    }

    const values = {
      _id: currentProjectForm?._id,
      projectId: currentProject?._id,
      status: "completed",
      values: mergedValues,
      iteration,
    }

    if (!settings?.generatePDFs) {
      await updateFunction({ variables: { values } })
    } else {
      const pdfVersion = formStates.find(
        (form) =>
          form.index === formIndex ||
          form._id === currentProjectForm?._id
      )?.pdfVersion
      const version = pdfVersion
        ? pdfVersionControl(pdfVersion)
        : "Not Generated"
      await updateFunction({
        variables: { values: { ...values, pdfVersion: version } },
      })

      // Use the form's persisted `index` (1-based in Furnace Lab), not the URL
      // array offset (`formNumber`), or PDF gen loads the wrong form's values.
      const pdfFormIndex =
        typeof currentProjectForm?.index === "number"
          ? currentProjectForm.index
          : formIndex

      await genProjectPDF({
        variables: {
          values: {
            index: pdfFormIndex,
            projectId: currentProject?._id,
            signatureFileLocation: "_formData",
            pdfFileLocation: `${settings?.spClientFolder}/${(
              currentProject?.clientName || ""
            ).replace(/\//g, " ")}/${
              currentProject?.reference
            }/App Forms`,
            name: `${currentProject?.reference}-${currentProjectForm?.name}`,
            spSiteUrl: settings?.spSiteUrl,
            spDocumentLibrary:
              settings?.spDocumentLibrary?.trim() || "Shared Documents",
          },
        },
      })
    }

    toast({ ...toastSuccess, description: "Form completed" })
    await refetch()
    await formStatusRefetch()
  }

  const onSubmitValid = async () => {
    setSubmitting(true)
    try {
      // Commit any debounced strokes, then let background uploads finish before PDF gen.
      flushAllPendingSignatureStrokes()
      const uploadsReady = await waitForPendingSignatureUploads(120000)
      if (!uploadsReady) {
        toast({
          ...toastFailed,
          description:
            "Signatures are still uploading. Please try again in a moment.",
        })
        return
      }

      const completedSig = getValues()?.completedBySignature
      if (!completedSig || completedSig === "CLEARED" || completedSig === "uploading") {
        toast({
          ...toastFailed,
          description: "Please sign this document before marking it as complete",
        })
        return
      }

      await runMarkAsComplete()
      onClose()
    } catch (e: any) {
      toast({
        ...toastFailed,
        description: e?.message || "Could not complete the form",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent rounded="20px" w="330px" pb="20px">
        <ModalHeader color="markAsCompleteModal.headerColor" fontSize="smm">
          Mark as complete?
        </ModalHeader>
        <ModalCloseButton onTouchEnd={onClose} />
        <ModalBody>
          <Text color="markAsCompleteModal.descColor" fontSize="smm" mb="15px">
            Please sign this document before marking it as complete.
          </Text>
          <SignatureApproval
            control={control}
            name="completedBySignature"
            label="Signature"
            validations={{ notEmpty: true }}
            setIsSignAndCompleteDisabled={setIsSignAndCompleteDisabled}
          />
          <Button
            w="full"
            mt="15px"
            bg="brePink"
            color="markAsCompleteModal.buttonColor"
            pl="5px"
            pr="15px"
            fontSize="smm"
            disabled={
              !getValues().completedBySignature ||
              getValues().completedBySignature === "uploading" ||
              isSignAndCompleteDisabled ||
              submitting
            }
            borderRadius="10px"
            isLoading={submitting || isSignAndCompleteDisabled}
            loadingText={
              submitting
                ? "Completing…"
                : isSignAndCompleteDisabled
                ? "Saving signature…"
                : "Saving…"
            }
            _hover={{
              bg: "reportFormHeader.buttonDisableBg",
              color: "reportFormHeader.buttonDisableColor",
            }}
            onClick={() => {
              if (!ipad) {
                handleSubmit(
                  onSubmitValid,
                  () =>
                    toast({
                      ...toastFailed,
                      description:
                        "Please complete all of the required fields",
                    })
                )()
              }
            }}
            onTouchEnd={() => {
              if (ipad) {
                handleSubmit(
                  onSubmitValid,
                  () =>
                    toast({
                      ...toastFailed,
                      description:
                        "Please complete all of the required fields",
                    })
                )()
              }
            }}
          >
            Sign and complete
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default MarkAsCompleteModal

export const markAsCompleteModalStyles = {
  markAsCompleteModal: {
    headerColor: "#313233",
    descColor: "#818197",
    buttonColor: "#ffffff",
  },
}
